import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import pick from 'lodash/pick';
import uniqBy from 'lodash/uniqBy';

import {MPContext} from 'common/mp-context';
import {ProjectFeatureFlag, SPECIAL_EVENTS} from 'common/report/constants';
import {BookmarkOverrides} from 'common/report/util/bookmark-overrides';
import {renameProperty} from 'common/report/util/properties';
import {relativeDatesToFullLengthDateRangeRollingBettweenBookmark} from 'common/util/date-converters';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {BookmarkDateRange, DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {Segfilter} from 'common/widgets/property-filter-menu/models/segfilter';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {ResourceType} from 'common/widgets/property-filter-menu/models/types';
import {bookmarkFilterByCohortToCohortFilters, getFilterByCohorts} from 'common/widgets/query-block/filter-menu/util';
import {isCohortSegment, isEventSegment, isUserSegment, Segment} from 'common/widgets/query-block/segment-menu/types';
import {FiltersOperator} from 'common/widgets/query-entry-section/types';
import {
  ChartType,
  DateUnit,
  isRelativeRollingDateRange,
  RetentionBookmarkParams,
  RetentionType,
} from 'common/report/util/retention/types';

import {cohortsToFilterByCohortAndGroupBy, convertOnStringToSegment, groupByToCohortsOrOn, onToGroupBy} from './format';

// TODO: figure out if/how I want to use this.
export interface QueryInterface {
  retentionType: RetentionType;
  bornEvent: string;
  bornFilters: RetentionBookmarkParams['born_filters'];
  bornFiltersOp: FiltersOperator;
  chartType: ChartType;
  dataGroupId: string;
  dateRange: BookmarkDateRange;
  dateUnit: DateUnit;
  cumulative: boolean;
  unbounded: boolean;
  percentageView: boolean;
  event: string;
  filters: RetentionBookmarkParams['filters'];
  filtersOp: FiltersOperator;
  filterByCohort: RetentionBookmarkParams['filter_by_cohort'];
  filterByEvent: RetentionBookmarkParams['filter_by_event'];
  groupBy: RetentionBookmarkParams['group_by'];
  segmentationEvent: string;
  sortColumn: string;
  sortDesc: boolean;
  querySampling: boolean;
}

const DEFAULTS: QueryInterface = Object.freeze({
  bornEvent: SPECIAL_EVENTS.ANY_EVENT,
  event: SPECIAL_EVENTS.ANY_EVENT,
  dataGroupId: null,
  bornFilters: [],
  bornFiltersOp: FiltersOperator.And,
  filters: [],
  filtersOp: FiltersOperator.And,
  filterByCohort: {},
  filterByEvent: {},
  chartType: `line`,
  cumulative: true,
  dateUnit: `day`,
  dateRange: {type: DateRangeType.RelativeAfter, from_date: {unit: TimeUnit.Day, value: 7}, to_date: `$now`},
  groupBy: [],
  percentageView: true,
  querySampling: false,
  retentionType: `birth`,
  segmentationEvent: null,
  sortColumn: `cohort`,
  sortDesc: false,
  unbounded: true,
});

const UI_ONLY_FIELDS = [`sort_desc`, `sort_column`, `percentage_view`, `chart_type`] as const;
// asserts that each of UI_ONLY_FIELDS are assignable to type `keyof RetentionBookmarkParams` while
// preserving the `as const` nature of the original declaration. exported to avoid
// the "unused variable" tsc error.
export const _assertUiOnlyFieldsValid: ReadonlyArray<keyof RetentionBookmarkParams> = UI_ONLY_FIELDS;

/**
 * RetentionQuery represents the UI state, the information necessary to make a query,
 * and the logic to translate from URLs and any version of bookmark into the current UI
 * state.
 */
export class RetentionQuery implements QueryInterface {
  public readonly retentionType: RetentionType;
  public readonly bornEvent: string;
  public readonly bornFilters: Segfilter[];
  public readonly bornFiltersOp: FiltersOperator;
  public readonly chartType: ChartType;
  public readonly dataGroupId: string;
  /** TODO: make dateRange an actual DateRange object */
  public readonly dateRange: BookmarkDateRange;
  public readonly dateUnit: DateUnit;
  public readonly cumulative: boolean;
  public readonly unbounded: boolean;
  public readonly percentageView: boolean;
  public readonly event: string;
  public readonly filters: Segfilter[];
  public readonly filtersOp: FiltersOperator;
  public readonly filterByCohort: RetentionBookmarkParams['filter_by_cohort'];
  public readonly filterByEvent: RetentionBookmarkParams['filter_by_event'];
  public readonly groupBy: Segment[];
  public readonly segmentationEvent: string;
  public readonly sortDesc: boolean;
  public readonly sortColumn: string;
  public querySampling: boolean;
  public context: MPContext;

  constructor(params: Partial<QueryInterface> = {}, context: MPContext = null) {
    this.context = context;
    Object.assign(
      this,
      DEFAULTS,
      getContextSensitiveDefaults(context),
      pick(params, Object.keys(DEFAULTS)),
      getStackedVizRolloutOverride(context),
    );
  }

  createFromUpdate(params: Partial<QueryInterface>): RetentionQuery {
    return new RetentionQuery(
      {
        ...this,
        ...params,
      },
      this.context,
    );
  }

  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  createFromBookmark(params: Partial<RetentionBookmarkParams>): RetentionQuery {
    const parsed: Partial<QueryInterface> = {};
    if (params.date_range) {
      if (isRelativeRollingDateRange(params.date_range)) {
        parsed.dateRange = relativeDatesToFullLengthDateRangeRollingBettweenBookmark({
          relativeFrom: Number(params.date_range.from),
          relativeTo: Number(params.date_range.to),
        });
      } else {
        parsed.dateRange = params.date_range;
      }
    }

    if (params.cohorts) {
      const parsedCohorts = cohortsToFilterByCohortAndGroupBy(params.cohorts, false);
      parsed.filterByCohort = parsedCohorts.filter_by_cohort;
      parsed.groupBy = parsedCohorts.group_by;
    } else {
      if (params.group_by) {
        parsed.groupBy = params.group_by;
      }

      if (params.filter_by_cohort) {
        parsed.filterByCohort = params.filter_by_cohort;
      }
    }

    if (params.filter_by_event) {
      parsed.filterByEvent = params.filter_by_event;
    }

    if (params.on) {
      parsed.groupBy = onToGroupBy(convertOnStringToSegment(params.on));
    }

    // Validate that (1) segmentationEvent corresponds to one of the events
    // (2) that it's not garbage. null and [] exist in the persisted bookmarks.
    if (params.segmentation_event && [params.born_event, params.event].includes(params.segmentation_event)) {
      parsed.segmentationEvent = params.segmentation_event;
    }

    if (params.retention_type === `compounded`) {
      // compounded retention is removed in favor of first time retention. 2020-03-11
      copy(params.event, parsed, `bornEvent`);
      copy(params.filters, parsed, `bornFilters`);
      copy(`birth`, parsed, `retentionType`);
    } else if (params.retention_type === `birth`) {
      copy(params.born_event, parsed, `bornEvent`);
      copy(params.born_filters, parsed, `bornFilters`);
      copy(params.born_filters_op, parsed, `bornFiltersOp`);
      copy(params.retention_type, parsed, `retentionType`);
    } else {
      copy(params.retention_type, parsed, `retentionType`);
    }

    // if unbounded retention is omit, default it to false
    if (params.retention_type === `compounded` || params.retention_type === `birth`) {
      parsed.unbounded = Boolean(params.unbounded_retention);
    }

    // No data translations to manage for these attributes.
    copy(params.event, parsed, `event`);
    copy(params.filters, parsed, `filters`);
    copy(params.filters_op, parsed, `filtersOp`);
    copy(params.date_unit, parsed, `dateUnit`);
    copy(params.percentage_view, parsed, `percentageView`);
    copy(params.sort_column, parsed, `sortColumn`);
    copy(params.sort_desc, parsed, `sortDesc`);
    copy(params.cumulative, parsed, `cumulative`);
    copy(params.chart_type, parsed, `chartType`);
    copy(params.data_group_id, parsed, `dataGroupId`);

    // Omit null, undefined and empty string so that the defaults can be filled in.
    return new RetentionQuery(omitNullAndUndefinedAndEmptyString(parsed), this.context);
  }

  /**
   * Construct a serializable object which can reproduce the query and UI state.
   *
   * @param {boolean} [forQuery = false] if true, omit UI only fields
   */
  generateBookmark(forQuery = false): RetentionBookmarkParams {
    /* eslint-disable camelcase */
    let bookmark: RetentionBookmarkParams = {
      date_unit: this.dateUnit,
      date_range: this.dateRange,
      chart_type: this.chartType,
      event: this.event,
      filters: this.filters,
      filters_op: this.filtersOp,
      born_event: this.bornEvent,
      born_filters: this.bornFilters,
      born_filters_op: this.bornFiltersOp,
      retention_type: this.retentionType,
      filter_by_cohort: this.filterByCohort,
      filter_by_event: this.filterByEvent,
      segmentation_event: this.segmentationEvent, // omit if empty?
      group_by: this.groupBy,
      data_group_id: this.dataGroupId,
      percentage_view: this.percentageView,
      cumulative: this.cumulative,
      unbounded_retention: this.unbounded,
      sort_column: this.sortColumn,
      sort_desc: this.sortDesc,
    };
    /* eslint-enable camelcase */

    if (this.retentionType !== `birth`) {
      bookmark = omit(bookmark, [`born_event`, `born_filters`]);
    }

    if (forQuery) {
      bookmark = omit(bookmark, UI_ONLY_FIELDS);
    }

    return bookmark;
  }

  /** Test query equality, ignoring UI-only parameters */
  queryEquals(other: RetentionQuery): boolean {
    return (
      this.querySampling === other.querySampling && isEqual(this.generateBookmark(true), other.generateBookmark(true))
    );
  }

  /** Test equality of UI state and bookmark, for persistence */
  equals(other: RetentionQuery): boolean {
    return isEqual(this.generateBookmark(), other.generateBookmark());
  }

  // Begin convenience methods for the view

  getCohorts(dedupe = true) {
    const {cohorts} = groupByToCohortsOrOn(this.groupBy);

    if (dedupe) {
      return uniqBy(cohorts, (c) => c.id);
    }
    return cohorts;
  }

  /**
   * Checks if the group by is on a user property or cohorts.
   */
  isGroupByUser() {
    const group = this.groupBy ? this.groupBy[0] : null;
    return group && (Array.isArray(group) || Object.prototype.hasOwnProperty.call(group, `user`));
  }

  isSegmented() {
    return this.groupBy.length > 0;
  }

  isSegmentedByCohort() {
    return this.isSegmented() && this.groupBy.some(isCohortSegment);
  }

  getGroupByName(group: Segment): string {
    if (isUserSegment(group)) {
      return renameProperty(group.user.propertyName);
    } else if (isEventSegment(group)) {
      return renameProperty(group.event.propertyName);
    } else {
      return `Cohort`;
    }
  }

  getSegmentName(): string {
    if (this.isSegmented() && !this.isSegmentedByCohort()) {
      return this.groupBy.map((group) => this.getGroupByName(group)).join(` / `);
      // TODO: revisit for B2B properties
    } else {
      return ``;
    }
  }

  isSegmentedByDataGroup(): boolean {
    return this.groupBy
      .map((group) => {
        if (isUserSegment(group)) {
          return group.user.dataGroupId !== undefined && group.user.dataGroupId !== null;
        } else if (isEventSegment(group)) {
          return group.event.dataGroupId !== undefined && group.event.dataGroupId !== null;
        }
        return false;
      })
      .reduce((acc, curr) => acc || curr, false);
  }

  applyOverrides(overrides: BookmarkOverrides): RetentionQuery {
    if (isEmpty(overrides)) {
      return this;
    }

    const {additionalFilters, dateRangeOverride, trendUnitOverride, chartTypeOverride} = overrides;
    const dateRange = dateRangeOverride ? {dateRange: new DateRange(dateRangeOverride).getBookmarkFormat()} : {};
    const dateUnit = trendUnitOverride ? {dateUnit: trendUnitOverride} : {};

    const additonalEventAndUserFilters = additionalFilters
      ? additionalFilters
          .filter((f) => [ResourceType.Event, ResourceType.User].includes(f.resourceType))
          .map((f) => new PropertyFilter(f))
      : [];
    const filterByEvent =
      additonalEventAndUserFilters.length > 0
        ? {
            filterByEvent: {
              operator: `and`,
              children: [
                ...((this.filterByEvent[`children`] as any[]) || []),
                ...additonalEventAndUserFilters.map((f) => ({[f.resourceType]: f})),
              ],
            },
          }
        : {};

    const additionalCohortFilters = additionalFilters
      ? additionalFilters.filter((f) => f.resourceType === ResourceType.Cohort).map((f) => new PropertyFilter(f))
      : [];
    const filterByCohort =
      additionalCohortFilters.length > 0
        ? {
            filterByCohort: getFilterByCohorts([
              ...bookmarkFilterByCohortToCohortFilters(this.filterByCohort),
              ...additionalCohortFilters,
            ]),
          }
        : {};

    return new RetentionQuery({
      ...this,
      ...(chartTypeOverride ? {chartType: chartTypeOverride} : {}),
      ...filterByCohort,
      ...filterByEvent,
      ...dateRange,
      ...dateUnit,
    });
  }
}

function copy<T, K extends keyof T>(value: T[K], dest: T, destName: K) {
  if (typeof value === `boolean` || value) {
    dest[destName] = value;
  }
}

// eslint-disable-next-line @typescript-eslint/ban-types
function omitNullAndUndefinedAndEmptyString<T extends object>(obj: T): Partial<T> {
  return omitBy(obj, function filterEmptyValues(value, _key) {
    return value === undefined || value === null || ((value as unknown) as string) === ``;
  });
}

function getContextSensitiveDefaults(context: MPContext): Partial<QueryInterface> {
  const defaults: Partial<QueryInterface> = {};

  if (context === undefined || context === null) {
    return defaults;
  }

  if (context.hasProjectFeatureFlag(ProjectFeatureFlag.QTSByDefault)) {
    defaults.querySampling = true;
  }

  if (context.projectId === 2214121) {
    // For tinder POC, anything retention was too expensive for them.
    defaults.bornEvent = `app_open`;
    defaults.event = `app_open`;
  }

  return defaults;
}

function getStackedVizRolloutOverride(context: MPContext) {
  if (!context) {
    return {};
  }

  if (isStackedVizOn(context)) {
    return {chartType: `table`};
  } else {
    return {};
  }
}

/**
 * Control rollout of the stacked visualization.
 *
 * Stacked viz is a default view with the line-chart over the data-table. It replaces the table view.
 */
export function isStackedVizOn(context: MPContext) {
  return context.hasProjectFeatureFlag(ProjectFeatureFlag.RetentionChartView) || context.organizationId % 10 < 5;
}
